import { ComponentProps, forwardRef, useContext } from 'react';
import clsx from 'clsx';

import { OpInputDuration } from 'new-components/DLS/OpInputDuration/OpInputDuration';
import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';

import { OpFormContext } from '../../OpFormContext';
import { OpForm } from '../../OpForm';

interface OpFormInputDurationProps
  extends Pick<
      ComponentProps<typeof OpForm.Item>,
      | 'name'
      | 'label'
      | 'rules'
      | 'tooltip'
      | 'validateTrigger'
      | 'initialValue'
    >,
    Omit<ComponentProps<typeof OpInputDuration>, 'name' | 'required'> {
  formItemClassName?: string;
  isLoading?: boolean;
}

export const OpFormInputDuration = forwardRef<
  HTMLInputElement,
  OpFormInputDurationProps
>(
  (
    {
      // Form Item props
      formItemClassName,
      name,
      label,
      rules,
      tooltip,
      validateTrigger,
      initialValue,

      // For skeleton loading state
      isLoading,

      // Wrapped element props
      disabled,

      testId,

      ...elementProps
    },
    ref,
  ) => {
    /** The Form is wrapped in a provider that passes the loading and readOnly
     * contexts to the component */
    const { isDataLoading, isReadOnly } = useContext(OpFormContext);

    return (
      <OpForm.Item
        className={clsx('op-form-input', formItemClassName)}
        name={name}
        label={label}
        rules={rules}
        tooltip={tooltip}
        validateTrigger={validateTrigger}
        initialValue={initialValue}
        testId={testId ? `${testId}-op-form-item` : undefined}
      >
        {isDataLoading || isLoading ? (
          /** A placeholder in the shape of the component
           * so we can immediately render the structure of the form while
           * data is loading */
          <OpSkeleton.Input active block />
        ) : (
          /** The actual component that is rendered when we have the data */
          <OpInputDuration
            disabled={isReadOnly || disabled}
            testId={testId}
            {...elementProps}
            ref={ref}
          />
        )}
      </OpForm.Item>
    );
  },
);
